import { REQUEST_ID_HEADER } from '@distribusion/portal-lib/constants';
import * as Sentry from '@sentry/react';
import { ErrorEvent, EventHint } from '@sentry/types';
import { isAxiosError } from 'axios';

// Global AxiosError caused event handler
export const handleAxiosErrorEvent = (event: ErrorEvent, hint: EventHint): Sentry.Event => {
  if (!hint.originalException || !isAxiosError(hint.originalException)) {
    return event;
  }

  const error = hint.originalException;
  const { response, config } = error;
  const { status, headers, data, request } = response || {};

  // Ignore 4** statuses
  if (status >= 400 && status < 500) {
    return;
  }

  if (typeof config?.data === 'string' && config.data.includes('password')) {
    config.data = '[REDACTED]';
  }

  event.fingerprint = ['request-error', status ? status.toString() : 'unknown'];
  event.contexts = {
    ...event.contexts,
    Axios: {
      url: request?.responseURL,
      config,
      status,
      headers,
      data,
    },
  };
  event.tags = {
    ...event.tags,
    'request-url': request?.responseURL || config?.url,
    'request-id': config?.headers?.[REQUEST_ID_HEADER],
  };

  return event;
};

export const formatSentryEventWithModuleMetadata = (event: ErrorEvent) => {
  try {
    const frames = event?.exception?.values?.[0].stacktrace?.frames || [];
    const sourceApps = frames
      .filter((frame) => frame.module_metadata && frame.module_metadata.app)
      .map((frame) => frame.module_metadata.app);
    const uniqueApps = Array.from(new Set(sourceApps));
    const sourceApp = uniqueApps[uniqueApps.length - 1];

    if (sourceApp) {
      event.extra = {
        ...event.extra,
        app: sourceApp,
      };
      event.tags = { ...event.tags, app: sourceApp };
    }
  } catch (e) {
    console.error('Failed to process module_metadata of Sentry event.\n', e);
  }

  return event;
};

export const handleBeforeSend: Sentry.BrowserOptions['beforeSend'] = (origEvent, hint) =>
  handleAxiosErrorEvent(formatSentryEventWithModuleMetadata(origEvent), hint);
